import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getErrorMessage } from "../utils/utilities";
import { getReportCacheProgress } from "../services/reportCache";

import * as reportCache from '../services/reportCache';
import toastService from "../services/toastService";

export interface CachePayload { 
    updated: object;
    latestChange: string;
}

const useDataCache = (projectId: string) => {
    const queryClient = useQueryClient();

    const queryKey = useMemo(() => {
        return ['data-cache', projectId];
    }, [projectId]);
   
    const { data, dataUpdatedAt, error, isFetching, refetch } = useQuery({
        queryKey,
        queryFn: () => getReportCacheProgress(projectId!),
        refetchOnMount: 'always',
        initialData: null,
    });

    const { mutate: onClearData } = useMutation({
        mutationFn: () => reportCache.remove(projectId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });

            toastService.success('Cache cleared!');
        },
        onError: (error) => {
            toastService.error(getErrorMessage(error));
        },
    })

    const { mutate: onUpdateData } = useMutation({
        mutationFn: ({  updated, latestChange }: CachePayload) => reportCache.store(projectId, updated, latestChange),
        onMutate: (variables) => {
            if (navigator.onLine) return;
            
            queryClient.setQueryData(queryKey, reportCache.generateCachePayload(variables.updated));
        },
    });

    const handleStoreDataInCache = (payload: CachePayload) => {
        queryClient.setQueryData(queryKey, payload);
    };

    return {
        data,
        loading: isFetching,
        error: error ? getErrorMessage(error) : '',
        lastUpdated: dataUpdatedAt,
        refetch,
        onClearData,
        onUpdateData,
        storeDataInCache: handleStoreDataInCache,
    };
};
 
export default useDataCache;